import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { Breadcrumbs } from '@/admin/ui/Breadcrumbs';
import { PageTitle } from '@/admin/ui/Page';
import { finalQuizTitle, QuizBadge } from '@/admin/ui/QuizBadge';
import { LockedQuizNotice } from '@/admin/ui/QuizLock';
import { QuestionEditor } from '@/admin/widgets/QuestionEditor';
import api from '@/api';

export const Route = createFileRoute('/admin/questions/new')({
  validateSearch: (
    s: Record<string, unknown>,
  ): { lesson: number; topic?: number } => ({
    lesson: Number(s.lesson),
    // No topic means the lesson's final quiz.
    topic: s.topic != null ? Number(s.topic) : undefined,
  }),
  component: NewQuestion,
});

function NewQuestion() {
  const { t } = useTranslation();
  const { lesson, topic } = Route.useSearch();
  const navigate = useNavigate();

  const lessonQuery = api.adminGetLesson.useQuery({
    path: { lesson_id: lesson },
  });
  // A topic quiz follows its topic; the final quiz follows the lesson.
  const topicQuery = api.adminGetTopic.useQuery(
    { path: { topic_id: topic ?? 0 } },
    { enabled: topic !== undefined },
  );

  if (lessonQuery.isLoading || topicQuery.isLoading)
    return <Message>{t('admin.common.loading')}</Message>;
  if (lessonQuery.error || !lessonQuery.data)
    return <Message $error>{t('admin.lessons.errorLoadingOne')}</Message>;
  if (topic !== undefined && (topicQuery.error || !topicQuery.data))
    return <Message $error>{t('admin.topics.errorLoadingOne')}</Message>;
  const parent = topic !== undefined ? topicQuery.data : lessonQuery.data;

  const backToList = () =>
    navigate({ to: '/admin/questions/', search: { lesson, topic } });

  return (
    <div>
      <Breadcrumbs
        path={
          topic !== undefined && topicQuery.data
            ? [
                ...topicQuery.data.path,
                { kind: 'topic', id: topic, title: topicQuery.data.title },
              ]
            : [
                ...lessonQuery.data.path,
                { kind: 'lesson', id: lesson, title: lessonQuery.data.title },
              ]
        }
        extra={[
          <Link key="quiz" to="/admin/questions/" search={{ lesson, topic }}>
            {topic !== undefined
              ? t('admin.quizBadge.topic')
              : finalQuizTitle(lessonQuery.data.title)}
          </Link>,
        ]}
        current={t('admin.questions.newQuestion')}
      />
      <TitleRow>
        <PageTitle>{t('admin.questions.newQuestion')}</PageTitle>
        <QuizBadge final={topic === undefined} />
      </TitleRow>
      {parent?.quiz_locked ? (
        <LockedQuizNotice parent={topic !== undefined ? 'topic' : 'lesson'} />
      ) : (
        <QuestionEditor
          lessonId={lesson}
          topicId={topic}
          quizLocked={false}
          onDone={backToList}
        />
      )}
    </div>
  );
}

const TitleRow = styled.div`
  align-items: center;
  display: flex;
  gap: 0.75rem;
  margin-bottom: var(--component-margin);
`;

const Message = styled.div<{ $error?: boolean }>`
  color: ${(p) => (p.$error ? 'var(--error)' : 'var(--grey-600)')};
  padding: 2rem;
  text-align: center;
`;
